
import { useCallback } from 'react';
import { Ball, SimulationConfig } from '@/types';
import { SpawnerPhysics } from '@/logic/modes/SpawnerPhysics';
import { AUDIO } from '@/services/AudioService';

export const useCollisionHandler = (
  configRef: React.MutableRefObject<SimulationConfig>,
  ballsRef: React.MutableRefObject<Ball[]>,
  ballPoolRef: React.MutableRefObject<any>,
  workerRef: React.MutableRefObject<Worker | null>,
  hueRef: React.MutableRefObject<number>,
  collisionEventsRef: React.MutableRefObject<any[]>,
  shakeRef: React.MutableRefObject<number>,
  setBallCount: (count: number) => void,
  setBounceCount: (v: (p: number) => number) => void
) => {
  const onCollision = useCallback((payload: any) => {
    const { ballIndex, targetIndex, type, pos, intensity } = payload;
    const now = Date.now();
    const effectiveCfg = configRef.current;
    const b = ballsRef.current[ballIndex];

    if (!b) return;

    if (type === 'WALL' && effectiveCfg.mode === 'SPAWNER') {
        const canSpawn = ballsRef.current.length < effectiveCfg.maxBalls;
        
        if (effectiveCfg.spawnOnWallCollision && canSpawn) {
            const spawns: Ball[] = [];
            
            if (effectiveCfg.spawnerEngineMode === 'BASIC') {
                const nb = SpawnerPhysics.handleBasicSpawn(b, effectiveCfg, now, pos, (overrides) => {
                    return ballPoolRef.current.get(`sp-w-${now}-${ballIndex}`, overrides.pos!, overrides.vel || SpawnerPhysics.getSpawnVelocity(effectiveCfg), overrides.radius!, overrides.color!, effectiveCfg.ballMass, effectiveCfg.ballStyle, undefined, overrides);
                });
                if (nb) spawns.push(nb);
            } else if (effectiveCfg.spawnerEngineMode === 'PATTERN' || effectiveCfg.spawnerEngineMode === 'CHAOS') {
                spawns.push(...SpawnerPhysics.handlePatternSpawn(effectiveCfg, pos, now, (overrides) => {
                    return ballPoolRef.current.get(`sp-p-${now}-${ballIndex}-${Math.random()}`, overrides.pos!, overrides.vel!, overrides.radius!, overrides.color!, effectiveCfg.ballMass, effectiveCfg.ballStyle, undefined, overrides);
                }, hueRef.current));
            }

            if (spawns.length > 0) {
                ballsRef.current.push(...spawns);
                if (workerRef.current) workerRef.current.postMessage({ type: 'ADD_BALLS', payload: { balls: spawns, now } });
                b.lastSpawnTime = now;
                setBallCount(ballsRef.current.length);
            }
        }

        if (effectiveCfg.fissionEnabled && canSpawn) {
            const splits = SpawnerPhysics.handleFission(b, effectiveCfg, now, (overrides) => {
                return ballPoolRef.current.get(`fiss-${now}-${ballIndex}-${Math.random()}`, overrides.pos!, overrides.vel || SpawnerPhysics.getSpawnVelocity(effectiveCfg), overrides.radius!, overrides.color!, overrides.mass || effectiveCfg.ballMass, effectiveCfg.ballStyle, undefined, overrides);
            });
            if (splits.length > 0) {
                ballsRef.current.push(...splits);
                if (workerRef.current) workerRef.current.postMessage({ type: 'ADD_BALLS', payload: { balls: splits, now } });
                setBallCount(ballsRef.current.length);
            }
        }
    } else if (type === 'BALL' && (effectiveCfg.mode === 'SPAWNER' || effectiveCfg.enableSpawner)) {
        const b2 = ballsRef.current[targetIndex];
        const canSpawn = ballsRef.current.length < effectiveCfg.maxBalls;

        if (effectiveCfg.fusionEnabled && b2) {
            const result = SpawnerPhysics.handleFusion(b, b2, effectiveCfg);
            if (result.merged && result.removed) {
                const rIdx = ballsRef.current.indexOf(result.removed);
                if (rIdx !== -1) {
                    ballsRef.current.splice(rIdx, 1);
                    if (workerRef.current) {
                        workerRef.current.postMessage({ type: 'CLEAR_BALLS' });
                        workerRef.current.postMessage({ type: 'ADD_BALLS', payload: { balls: ballsRef.current, now } });
                    }
                    setBallCount(ballsRef.current.length);
                    return; 
                }
            }
        }

        if (effectiveCfg.spawnOnBallCollision && canSpawn && now - (b.lastSpawnTime || 0) > 150) {
            const finalVel = SpawnerPhysics.getSpawnVelocity(effectiveCfg);
            const nb = ballPoolRef.current.get(`sp-b-${now}-${ballIndex}`, { ...pos }, finalVel, effectiveCfg.ballRadius, b.color, effectiveCfg.ballMass, effectiveCfg.ballStyle);
            ballsRef.current.push(nb);
            if (workerRef.current) workerRef.current.postMessage({ type: 'ADD_BALLS', payload: { balls: [nb], now } });
            
            b.lastSpawnTime = now;
            if (b2) b2.lastSpawnTime = now; 
            
            setBallCount(ballsRef.current.length);
        }
    }

    collisionEventsRef.current.push({ 
        id: `col-app-${now}-${ballIndex}`, 
        pos: { ...pos }, 
        startTime: now, 
        radius: b.radius * 2.5, 
        color: b.color, 
        effectType: effectiveCfg.collisionEffect, 
        duration: effectiveCfg.collisionEffectDuration || 800 
    });

    if (intensity > 0.2) {
        AUDIO.playCollisionSound(intensity, effectiveCfg);
        shakeRef.current = Math.max(shakeRef.current, 2 * effectiveCfg.shakeIntensityMultiplier);
    }
    
    setBounceCount(prev => prev + 1);
  }, [setBallCount, setBounceCount]);

  return { onCollision };
};
